import React, { useState } from 'react';
import { useSite } from '../../context/SiteContext';
import { History, RotateCcw, CheckCircle2, Clock, X, ShieldCheck } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface VersionSnapshot {
  id: string;
  name: string;
  timestamp: string;
  type: 'auto' | 'manual' | 'publish';
  description: string;
}

export const VersionHistoryModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { activeSite, updateSiteData } = useSite();

  const [versions, setVersions] = useState<VersionSnapshot[]>([
    {
      id: 'v-current',
      name: 'Mevcut Canlı Versiyon',
      timestamp: 'Bugün, Şimdi',
      type: 'publish',
      description: 'En son yapılan içerik ve tasarım güncellemeleri.',
    },
    {
      id: 'v-2',
      name: 'Otomatik Yedekleme (Hizmetler Eklendi)',
      timestamp: 'Bugün, 14:20',
      type: 'auto',
      description: '4 adet kurumsal hizmet ve fiyatlandırma kaydedildi.',
    },
    {
      id: 'v-1',
      name: 'İlk Kurulum & AI Site Planı',
      timestamp: 'Dün, 18:45',
      type: 'manual',
      description: 'Firma bilgileri ve temel kurumsal tasarım şablonu oluşturuldu.',
    },
  ]);

  const [restoredId, setRestoredId] = useState<string | null>(null);

  if (!isOpen || !activeSite) return null;

  const handleRollback = (ver: VersionSnapshot) => {
    setRestoredId(ver.id);
    setTimeout(() => {
      setRestoredId(null);
      onClose();
    }, 1000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/75 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-5 shadow-2xl border border-slate-200">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2 text-blue-600">
            <History className="w-5 h-5" />
            <h2 className="text-base font-bold text-slate-900">Versiyon Geçmişi & Geri Yükleme</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-slate-500 leading-relaxed">
          Sitenizde yapılan her önemli değişiklik otomatik olarak yedeklenir. İstediğiniz bir versiyona tek tıkla geri dönebilirsiniz (Rollback).
        </p>

        <div className="space-y-3">
          {versions.map((ver, idx) => (
            <div
              key={ver.id}
              className={`p-4 rounded-xl border transition flex items-center justify-between ${
                idx === 0
                  ? 'border-blue-200 bg-blue-50/50'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-xs text-slate-900">{ver.name}</span>
                  {idx === 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-bold">
                      Aktif
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                  <Clock className="w-3 h-3" />
                  <span>{ver.timestamp}</span>
                </div>
                <p className="text-[11px] text-slate-500">{ver.description}</p>
              </div>

              {idx !== 0 && (
                <button
                  type="button"
                  onClick={() => handleRollback(ver)}
                  disabled={restoredId !== null}
                  className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-blue-600 hover:text-white text-slate-700 text-xs font-bold transition flex items-center gap-1 shrink-0"
                >
                  <RotateCcw className="w-3 h-3" />
                  <span>{restoredId === ver.id ? 'Yükleniyor...' : 'Geri Yükle'}</span>
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="pt-3 border-t border-slate-100 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};
